import { DatePipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { CertificateRecord, PublicEvent, WrpStatus } from "../transparency-log";
import { WatchFinding } from "../watch-rules";
import {
  WatchContext,
  WrpRow,
  certificateLabel,
  eventLink,
  matchesText,
  shortFingerprint,
  subjectName,
  wrpLink,
  SIGNATURE_LABELS,
  recordName,
  replacementNote,
  requestSummary,
  revocationReasonLabel,
} from "../watch-view-model";
import { CertificateInspector } from "./certificate-dialog.component";

type StatusFilter = "all" | WrpStatus;

@Component({
  selector: "watch-register-view",
  imports: [DatePipe, MatIconModule],
  templateUrl: "./register-view.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterViewComponent {
  private readonly inspector = inject(CertificateInspector);

  readonly rows = input.required<WrpRow[]>();
  readonly findings = input.required<WatchFinding[]>();
  readonly context = input.required<WatchContext>();
  readonly selectedWrpId = model<string | undefined>();
  readonly openFinding = output<string>();

  readonly query = signal("");
  readonly status = signal<StatusFilter>("all");
  readonly filters: Array<{ value: StatusFilter; label: string }> = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "suspended", label: "Suspended" },
    { value: "cancelled", label: "Cancelled" },
    { value: "historic", label: "Historic" },
  ];

  readonly visible = computed(() =>
    this.rows().filter(
      (row) =>
        (this.status() === "all" || row.status === this.status()) &&
        matchesText(this.query(), row.name, row.wrpId),
    ),
  );

  readonly counts = computed(() => {
    const count = (status: WrpStatus) =>
      this.rows().filter((row) => row.status === status).length;
    return {
      all: this.rows().length,
      active: count("active"),
      suspended: count("suspended"),
      cancelled: count("cancelled"),
      historic: count("historic"),
    };
  });

  readonly selected = computed<WrpRow | undefined>(
    () =>
      this.rows().find((row) => row.wrpId === this.selectedWrpId()) ??
      this.visible()[0],
  );

  readonly selectedFindings = computed(() => {
    const wrpId = this.selected()?.wrpId;
    return this.findings().filter((finding) => finding.wrpId === wrpId);
  });

  label = certificateLabel;
  reason = revocationReasonLabel;
  signatureLabels = SIGNATURE_LABELS;
  subject = subjectName;
  short = shortFingerprint;
  requests = requestSummary;
  recordName = recordName;

  json(value: unknown): string {
    return JSON.stringify(value, null, 2);
  }

  replacement(record: CertificateRecord): string | undefined {
    return replacementNote(this.context(), record);
  }

  inspect(record: CertificateRecord): void {
    this.inspector.open(record, this.context());
  }

  eventLink(event: PublicEvent): string {
    return eventLink(this.context(), event);
  }

  wrpLink(wrpId: string): string {
    return wrpLink(this.context(), wrpId);
  }

  trackCertificate(record: CertificateRecord): string {
    return record.fingerprint;
  }

  onQuery(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }
}
